import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { TailSpin } from 'react-loader-spinner';

const LEAVE_STATUS_LABEL = {
    approval_pending: "Approval Pending",
    on_leave: "Currently On Leave",
};

const formatTime12Hour = (time) => {
    if (!time) return "";
    const [hourStr, minuteStr] = time.split(":");
    let hour = parseInt(hourStr, 10);
    if (isNaN(hour)) return time;
    const period = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minuteStr} ${period}`;
};

const formatLeaveDateTime = (leg) => {
    if (!leg) return "-";
    const parts = [leg.date, formatTime12Hour(leg.time)].filter(Boolean).join(" ");
    return leg.travel_mode ? `${parts} (${leg.travel_mode})` : parts || "-";
};

const formatAppliedOn = (applied_on) => {
    if (!applied_on) return "-";
    const date = new Date(applied_on);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

class DeleteHostModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: "loading", // loading | select_students | confirm_delete | leave_warning
            linked_students: [],
            selected_student_ids: [],
            in_progress_leaves: [],
            error_msg: "",
            isProcessing: false,
        };
    }

    componentDidMount() {
        this.loadLinkedStudents();
    }

    loadLinkedStudents = () => {
        const { hostData } = this.props;
        this.setState({ step: "loading", error_msg: "" });
        this.props.onGetHostLinkedStudents(hostData.host_id).then((response) => {
            if (response.success === true) {
                const students = response.data?.students || response.students || [];
                if (students.length > 0) {
                    this.setState({ step: "select_students", linked_students: students, selected_student_ids: [] });
                } else {
                    this.checkInProgressLeaves([]);
                }
            } else {
                this.setState({ error_msg: response.message || "Failed to fetch linked students." });
                this.props.onHideModal();
            }
        });
    };

    checkInProgressLeaves = (student_ids) => {
        const { hostData } = this.props;
        this.setState({ step: "loading" });
        this.props.onCheckHostInProgressLeaves(hostData.host_id, student_ids).then((response) => {
            if (response.success === true) {
                const in_progress = response.data?.in_progress || [];
                if (in_progress.length > 0) {
                    this.setState({ step: "leave_warning", in_progress_leaves: in_progress, selected_student_ids: student_ids });
                } else {
                    this.setState({ step: "confirm_delete", selected_student_ids: student_ids });
                }
            } else {
                this.setState({ error_msg: response.message || "Failed to check in-progress leaves." });
                this.props.onHideModal();
            }
        });
    };

    handleToggleStudent = (student_id) => {
        const { selected_student_ids } = this.state;
        const updated = selected_student_ids.includes(student_id)
            ? selected_student_ids.filter((id) => id !== student_id)
            : [...selected_student_ids, student_id];
        this.setState({ selected_student_ids: updated });
    };

    handleNextFromSelection = () => {
        if (this.state.selected_student_ids.length === 0) {
            this.setState({ error_msg: "Please select at least one student to continue." });
            return;
        }
        this.setState({ error_msg: "" });
        this.checkInProgressLeaves(this.state.selected_student_ids);
    };

    handleBackFromWarning = () => {
        if (this.state.linked_students.length > 0) {
            this.setState({ step: "select_students", error_msg: "" });
        } else {
            this.props.onHideModal();
        }
    };

    handleConfirmDelete = () => {
        const { hostData } = this.props;
        this.setState({ isProcessing: true });
        this.props.onManagerDeleteStudentHost(hostData.host_id, this.state.selected_student_ids)
            .then(() => {
                this.setState({ isProcessing: false });
                this.props.onHideModal();
            })
            .catch(() => {
                this.setState({ isProcessing: false });
            });
    };

    renderStudentSelection() {
        const { hostData } = this.props;
        const { linked_students, selected_student_ids, error_msg } = this.state;
        return (
            <React.Fragment>
                <Modal.Body>
                    <p>
                        The host "<strong>{hostData?.host_name}</strong>" is linked to the following students. Select the student(s) you want to remove this host from:
                    </p>
                    <div className="d-flex flex-wrap" style={{ gap: '1.5rem' }}>
                        {linked_students.map((student) => (
                            <Form.Check
                                key={student.student_id}
                                type="checkbox"
                                id={`student-${student.student_id}`}
                                label={student.student_name}
                                checked={selected_student_ids.includes(student.student_id)}
                                onChange={() => this.handleToggleStudent(student.student_id)}
                                className="mb-2"
                            />
                        ))}
                    </div>
                    {error_msg && <p className="text-danger mt-2"><small>{error_msg}</small></p>}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-primary btn-sm" onClick={this.handleNextFromSelection}>Next</button>
                </Modal.Footer>
            </React.Fragment>
        );
    }

    renderLeaveWarning() {
        const { hostData } = this.props;
        const { in_progress_leaves, isProcessing } = this.state;
        return (
            <React.Fragment>
                <Modal.Body style={{ position: "relative" }}>
                    <div className="text-center mb-3">
                        <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <p>
                        The host "<strong>{hostData?.host_name}</strong>" has active leave records for the following student(s). If you still want to delete this host, then click on the delete button.
                    </p>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>Student Name</th>
                                    <th>Leave Status</th>
                                    <th>Departure</th>
                                    <th>Return</th>
                                    <th>Applied On</th>
                                </tr>
                            </thead>
                            <tbody>
                                {in_progress_leaves.map((leave) => (
                                    <tr key={leave.student_id}>
                                        <td>{leave.student_name}</td>
                                        <td>{LEAVE_STATUS_LABEL[leave.status] || leave.status}</td>
                                        <td className="text-nowrap">{formatLeaveDateTime(leave.departure)}</td>
                                        <td className="text-nowrap">{formatLeaveDateTime(leave.return)}</td>
                                        <td className="text-nowrap">{formatAppliedOn(leave.applied_on)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {isProcessing && this.renderLoaderOverlay()}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary btn-sm" onClick={this.handleBackFromWarning} disabled={isProcessing}>Back</button>
                    <Button className="btn-sm px-7 btn-danger" onClick={this.handleConfirmDelete} disabled={isProcessing}>
                        Continue Delete
                    </Button>
                </Modal.Footer>
            </React.Fragment>
        );
    }

    renderConfirmDelete() {
        const { hostData } = this.props;
        const { isProcessing } = this.state;
        return (
            <React.Fragment>
                <Modal.Body style={{ position: "relative" }}>
                    <div className="text-center">
                        <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                        <h4 className="mt-3">Are you sure?</h4>
                        <p className="text-muted">
                            Do you want to delete the host "{hostData?.host_name}"?
                        </p>
                        <p className="text-danger">
                            <small>This action cannot be undone.</small>
                        </p>
                    </div>
                    {isProcessing && this.renderLoaderOverlay()}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn-sm px-7 btn-danger" onClick={this.handleConfirmDelete} disabled={isProcessing}>
                        Delete
                    </Button>
                </Modal.Footer>
            </React.Fragment>
        );
    }

    renderLoading() {
        return (
            <Modal.Body style={{ minHeight: "150px", position: "relative" }}>
                {this.renderLoaderOverlay()}
            </Modal.Body>
        );
    }

    renderLoaderOverlay() {
        return (
            <div style={{ backdropFilter: "blur(5px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "#b8b8b82b", borderRadius: "0.3rem" }}>
                <div>
                    <TailSpin
                        visible={true}
                        height="80"
                        width="80"
                        color="#3693d0"
                        ariaLabel="tail-spin-loading"
                        radius="1"
                        wrapperStyle={{}}
                        wrapperClass=""
                    />
                </div>
            </div>
        );
    }

    render() {
        const { show, onHideModal } = this.props;
        const { step } = this.state;

        return (
            <Modal
                show={show}
                onHide={onHideModal}
                centered
                size={step === "leave_warning" ? "lg" : undefined}
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Host</Modal.Title>
                </Modal.Header>
                {step === "loading" && this.renderLoading()}
                {step === "select_students" && this.renderStudentSelection()}
                {step === "leave_warning" && this.renderLeaveWarning()}
                {step === "confirm_delete" && this.renderConfirmDelete()}
            </Modal>
        );
    }
}
export default DeleteHostModal;
